import {
  DEFAULT_WEB_SUMMIT_SCENARIO_ID,
  WEB_SUMMIT_SHARED_DEMO_CASE_ID,
  buildAcceptanceImpactPath as buildAcceptanceScenariosPath,
  buildAcceptanceScenarioProofPath,
  buildAcceptanceWalkthroughPath,
  getWebSummitDemoScenarioById,
  isWebSummitScenarioCaseId,
} from './webSummitDemoScenarios';

export const DEFAULT_WEB_SUMMIT_CASE_ID = WEB_SUMMIT_SHARED_DEMO_CASE_ID;

export type ResolutionProofCaseSource = 'demo_fixture' | 'live_proof' | 'neutral_fallback';

export type ResolutionProofCase = {
  caseId: string;
  site: string;
  zone: string;
  pattern: string;
  windowLabel: string;
  baselineCount: number | null;
  followUpCount: number | null;
  changePercent: number | null;
  trustPosture: string;
  outcomeStatus: string;
  correctiveActionStatus: string;
  evidencePackStatus: string;
  reliabilityPackStatus: string;
  handoffBundleStatus: string;
  timestamps: {
    observedAt?: string | null;
    followUpAt?: string | null;
    exportedAt?: string | null;
  };
  diagnostics: {
    orgId?: string | null;
    siteId?: string | null;
    zoneId?: string | null;
    deviceId?: string | null;
    cameraId?: string | null;
    incidentType?: string | null;
    reportFrom?: string | null;
    reportTo?: string | null;
  };
  incidentLabel?: string;
  cameraSourceLabel?: string;
  modeLabel?: string;
  demoOutcomeNote?: string;
  demonstratedWorkflow?: string;
  title: string;
  subtitle: string;
  impactSentence: string;
  useCases: string[];
  source: ResolutionProofCaseSource;
};

export type ImpactSummaryModel = {
  caseId: string;
  primaryMetric: string;
  primaryMetricLabel: string;
  beforeAfterLabel: string;
  actionLabel: string;
  outcomeLabel: string;
  valueSignal: string;
  proofHref: string;
  caseStudyHref: string;
  source: ResolutionProofCaseSource;
  eyebrow: string;
  headline: string;
  subheadline: string;
  contextLine: string;
  primaryCtaLabel: string;
  secondaryCtaLabel: string;
  impactCards: Array<{
    title: string;
    detail: string;
    cta?: string;
    featured?: boolean;
  }>;
  useCases: string[];
  narrativeTitle: string;
  narrativeDetail: string;
  valueTitle: string;
  valueDetail: string;
};

function parseCount(value: string | null | undefined): number | null {
  const normalized = String(value || '').trim();
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function humanizeToken(value: string | null | undefined, fallback: string): string {
  const normalized = String(value || '').trim();
  if (!normalized) return fallback;
  return normalized
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function buildWindowLabel(reportFrom?: string | null, reportTo?: string | null): string {
  const from = String(reportFrom || '').trim();
  const to = String(reportTo || '').trim();
  if (from && to) return `${from} → ${to}`;
  if (from) return `From ${from}`;
  if (to) return `Until ${to}`;
  return 'Current proof window';
}

function formatPercent(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return 'Not measured yet';
  return `${Math.round(value)}%`;
}

function formatBeforeAfterLabel(baselineCount: number | null, followUpCount: number | null): string {
  if (baselineCount == null || followUpCount == null) return 'Not measured yet';
  return `${baselineCount} → ${followUpCount}`;
}

function _buildCaseQueryParams(search: string, proofCase: ResolutionProofCase): URLSearchParams {
  const params = new URLSearchParams(search);
  if (!params.get('caseId')) params.set('caseId', proofCase.caseId);
  if (!params.get('case_id')) params.set('case_id', proofCase.caseId);
  if (proofCase.diagnostics.orgId && !params.get('org_id')) params.set('org_id', proofCase.diagnostics.orgId);
  if (proofCase.diagnostics.siteId && !params.get('site_id')) params.set('site_id', proofCase.diagnostics.siteId);
  if (proofCase.diagnostics.zoneId && !params.get('zone_id')) params.set('zone_id', proofCase.diagnostics.zoneId);
  if (proofCase.diagnostics.deviceId && !params.get('device_id')) params.set('device_id', proofCase.diagnostics.deviceId);
  if (proofCase.diagnostics.incidentType && !params.get('incident_type')) {
    params.set('incident_type', proofCase.diagnostics.incidentType);
  }
  if (proofCase.diagnostics.reportFrom && !params.get('report_from')) {
    params.set('report_from', proofCase.diagnostics.reportFrom);
  }
  if (proofCase.diagnostics.reportTo && !params.get('report_to')) {
    params.set('report_to', proofCase.diagnostics.reportTo);
  }
  if (proofCase.diagnostics.cameraId && !params.get('camera_id')) params.set('camera_id', proofCase.diagnostics.cameraId);
  return params;
}

export function isWebSummitDemoProofCaseId(caseIdRaw: string | null | undefined): boolean {
  return isWebSummitScenarioCaseId(caseIdRaw);
}

export function buildWebSummitDemoProofCase(search: string, caseIdRaw?: string | null): ResolutionProofCase {
  const params = new URLSearchParams(search);
  const requestedCaseId = String(caseIdRaw || params.get('caseId') || params.get('case_id') || DEFAULT_WEB_SUMMIT_CASE_ID).trim();
  const caseId = isWebSummitScenarioCaseId(requestedCaseId) ? DEFAULT_WEB_SUMMIT_CASE_ID : requestedCaseId || DEFAULT_WEB_SUMMIT_CASE_ID;
  const scenario = getWebSummitDemoScenarioById(params.get('scenarioId') || params.get('scenario_id') || DEFAULT_WEB_SUMMIT_SCENARIO_ID);

  return {
    caseId,
    site: scenario.site,
    zone: scenario.zone,
    pattern: scenario.pattern,
    windowLabel: scenario.windowLabel,
    baselineCount: null,
    followUpCount: null,
    changePercent: null,
    trustPosture: 'Ready to prepare',
    outcomeStatus: 'Not measured in looped demo',
    correctiveActionStatus: 'Action logged',
    evidencePackStatus: 'Ready to prepare',
    reliabilityPackStatus: 'Ready for review',
    handoffBundleStatus: 'Available',
    timestamps: {
      observedAt: '2026-05-07T09:00',
      followUpAt: null,
      exportedAt: '2026-05-07T09:15',
    },
    diagnostics: {
      orgId: 'acme',
      siteId: scenario.siteId,
      zoneId: scenario.zoneId,
      deviceId: null,
      cameraId: scenario.cameraSource,
      incidentType: scenario.incidentType,
      reportFrom: '2026-05-07T09:00',
      reportTo: '2026-05-07T09:15',
    },
    incidentLabel: scenario.family,
    cameraSourceLabel: scenario.cameraLabel,
    modeLabel: scenario.modeLabel,
    demoOutcomeNote:
      'The source feed is looped, so behavioral reduction over time is not evaluated in this demo.',
    demonstratedWorkflow: 'Detection → review → action logging → proof-package readiness',
    title: 'Live RTSP pedestrian/forklift demo',
    subtitle:
      'This demo shows the workflow from live camera feed to detection, review, action logging, and proof-package readiness.',
    impactSentence:
      'The looped RTSP demo shows detection, review, action logging, and proof-package readiness without claiming outcome improvement over time.',
    useCases: ['WebSummit Demo', 'Pedestrian/Forklift Interaction', 'RTSP Monitoring', 'Safety Review'],
    source: 'demo_fixture',
  };
}

export function buildNeutralResolutionProofCase(search: string, caseIdRaw?: string | null): ResolutionProofCase {
  const params = new URLSearchParams(search);
  const caseId = String(caseIdRaw || params.get('caseId') || params.get('case_id') || '').trim() || 'current-proof-case';
  const reportFrom = String(params.get('report_from') || '').trim() || null;
  const reportTo = String(params.get('report_to') || '').trim() || null;

  return {
    caseId,
    site: humanizeToken(params.get('site_id'), 'Selected site'),
    zone: humanizeToken(params.get('zone_id'), 'Selected zone'),
    pattern: humanizeToken(params.get('incident_type'), 'Current recurring risk'),
    windowLabel: buildWindowLabel(reportFrom, reportTo),
    baselineCount: parseCount(params.get('baseline_count')),
    followUpCount: parseCount(params.get('followup_count')),
    changePercent: parseCount(params.get('change_percent')),
    trustPosture: 'Needs review',
    outcomeStatus: 'Not measured yet',
    correctiveActionStatus: 'Waiting',
    evidencePackStatus: 'Waiting',
    reliabilityPackStatus: 'Waiting',
    handoffBundleStatus: 'Not ready yet',
    timestamps: {
      observedAt: reportFrom,
      followUpAt: reportTo,
      exportedAt: null,
    },
    diagnostics: {
      orgId: String(params.get('org_id') || '').trim() || null,
      siteId: String(params.get('site_id') || '').trim() || null,
      zoneId: String(params.get('zone_id') || '').trim() || null,
      deviceId: String(params.get('device_id') || '').trim() || null,
      cameraId: String(params.get('camera_id') || '').trim() || null,
      incidentType: String(params.get('incident_type') || '').trim() || null,
      reportFrom,
      reportTo,
    },
    title: 'Resolution case study',
    subtitle: 'Derived from the current proof scope.',
    impactSentence: 'Business impact will be populated as proof measurements and follow-up data become available.',
    useCases: ['Warehousing', 'Manufacturing', 'Yard Safety', 'Pedestrian/Forklift Interaction', 'PPE Compliance'],
    source: 'neutral_fallback',
  };
}

export function buildOperationalResolutionProofCase(input: {
  caseId: string;
  site?: string | null;
  zone?: string | null;
  pattern?: string | null;
  windowLabel?: string | null;
  baselineCount?: number | null;
  followUpCount?: number | null;
  changePercent?: number | null;
  trustPosture?: string | null;
  outcomeStatus?: string | null;
  correctiveActionStatus?: string | null;
  evidencePackStatus?: string | null;
  reliabilityPackStatus?: string | null;
  handoffBundleStatus?: string | null;
  impactSentence?: string | null;
  orgId?: string | null;
  siteId?: string | null;
  zoneId?: string | null;
  deviceId?: string | null;
  cameraId?: string | null;
  incidentType?: string | null;
  reportFrom?: string | null;
  reportTo?: string | null;
  observedAt?: string | null;
  followUpAt?: string | null;
  exportedAt?: string | null;
  source?: ResolutionProofCaseSource;
}): ResolutionProofCase {
  return {
    caseId: String(input.caseId || '').trim() || 'current-proof-case',
    site: String(input.site || '').trim() || 'Unavailable',
    zone: String(input.zone || '').trim() || 'Unavailable',
    pattern: String(input.pattern || '').trim() || 'Unavailable',
    windowLabel: String(input.windowLabel || '').trim() || 'Unavailable',
    baselineCount: input.baselineCount ?? null,
    followUpCount: input.followUpCount ?? null,
    changePercent: input.changePercent ?? null,
    trustPosture: String(input.trustPosture || '').trim() || 'Needs review',
    outcomeStatus: String(input.outcomeStatus || '').trim() || 'Waiting',
    correctiveActionStatus: String(input.correctiveActionStatus || '').trim() || 'Waiting',
    evidencePackStatus: String(input.evidencePackStatus || '').trim() || 'Waiting',
    reliabilityPackStatus: String(input.reliabilityPackStatus || '').trim() || 'Waiting',
    handoffBundleStatus: String(input.handoffBundleStatus || '').trim() || 'Not ready yet',
    timestamps: {
      observedAt: input.observedAt ?? null,
      followUpAt: input.followUpAt ?? null,
      exportedAt: input.exportedAt ?? null,
    },
    diagnostics: {
      orgId: input.orgId ?? null,
      siteId: input.siteId ?? null,
      zoneId: input.zoneId ?? null,
      deviceId: input.deviceId ?? null,
      cameraId: input.cameraId ?? null,
      incidentType: input.incidentType ?? null,
      reportFrom: input.reportFrom ?? null,
      reportTo: input.reportTo ?? null,
    },
    title: 'Resolution case study',
    subtitle: 'Derived from the current proof scope.',
    impactSentence:
      String(input.impactSentence || '').trim() ||
      'Business impact will be populated as proof measurements and follow-up data become available.',
    useCases: ['Warehousing', 'Manufacturing', 'Yard Safety', 'Pedestrian/Forklift Interaction', 'PPE Compliance'],
    source: input.source || 'live_proof',
  };
}

export function resolveResolutionProofCase(search: string, caseIdRaw?: string | null): ResolutionProofCase {
  const params = new URLSearchParams(search);
  const routeCaseId = String(caseIdRaw || params.get('caseId') || params.get('case_id') || '').trim();
  if (isWebSummitDemoProofCaseId(routeCaseId)) {
    return buildWebSummitDemoProofCase(search, routeCaseId);
  }
  return buildNeutralResolutionProofCase(search, routeCaseId);
}

export function buildAcceptanceImpactPath(search: string, _caseIdRaw?: string | null): string {
  return buildAcceptanceScenariosPath(search);
}

export function buildAcceptanceCaseStudyPath(caseId: string, search: string): string {
  const params = new URLSearchParams(search);
  const scenarioId = params.get('scenarioId') || params.get('scenario_id') || DEFAULT_WEB_SUMMIT_SCENARIO_ID;
  return buildAcceptanceWalkthroughPath(scenarioId, search);
}

export function buildAcceptanceProofPath(caseId: string, search: string): string {
  const params = new URLSearchParams(search);
  const scenarioId = params.get('scenarioId') || params.get('scenario_id') || DEFAULT_WEB_SUMMIT_SCENARIO_ID;
  return buildAcceptanceScenarioProofPath(scenarioId, search);
}

export function deriveImpactSummary(proofCase: ResolutionProofCase, search = ''): ImpactSummaryModel {
  const primaryMetric = formatPercent(proofCase.changePercent);
  const beforeAfterLabel = formatBeforeAfterLabel(proofCase.baselineCount, proofCase.followUpCount);
  const actionLabel = proofCase.correctiveActionStatus || 'Waiting';
  const outcomeLabel = proofCase.outcomeStatus || 'Not measured yet';
  const valueSignal = proofCase.changePercent != null && proofCase.changePercent > 0 ? 'Illustrative only' : 'Not calculated yet';

  return {
    caseId: proofCase.caseId,
    primaryMetric,
    primaryMetricLabel: 'workflow outcome',
    beforeAfterLabel,
    actionLabel,
    outcomeLabel,
    valueSignal,
    proofHref: buildAcceptanceScenarioProofPath(DEFAULT_WEB_SUMMIT_SCENARIO_ID, search),
    caseStudyHref: buildAcceptanceWalkthroughPath(DEFAULT_WEB_SUMMIT_SCENARIO_ID, search),
    source: proofCase.source,
    eyebrow: 'Live RTSP Demo',
    headline: 'Live pedestrian/forklift safety demo',
    subheadline:
      'A local RTSP camera feed is monitored for pedestrian activity inside a forklift operating area, then converted into a reviewable incident and proof-ready workflow.',
    contextLine: [proofCase.cameraSourceLabel || 'Local RTSP demo feed', proofCase.incidentLabel || proofCase.pattern, proofCase.modeLabel || proofCase.windowLabel].join(' · '),
    primaryCtaLabel: 'Open Pedestrian/Forklift Demo',
    secondaryCtaLabel: 'View Proof Package',
    impactCards: [
      {
        title: 'Connected',
        detail: 'camera feed',
        cta: 'Open demo',
        featured: true,
      },
      {
        title: 'Active',
        detail: 'risk detection',
      },
      {
        title: actionLabel,
        detail: 'corrective action state',
      },
      {
        title: outcomeLabel,
        detail: 'looped-demo measurement status',
      },
      {
        title: proofCase.evidencePackStatus || 'Ready to prepare',
        detail: 'proof package state',
      },
    ],
    useCases: proofCase.useCases,
    narrativeTitle: 'Live workflow',
    narrativeDetail:
      proofCase.source === 'demo_fixture'
        ? 'The demo value is camera feed to detection, incident creation, review, action logging, and proof-package readiness.'
        : 'This view summarizes operational proof state into a pilot-ready workflow without disconnecting from the underlying case.',
    valueTitle: 'Demo note',
    valueDetail: proofCase.impactSentence,
  };
}
